'use client'

import { X24 } from '@vinted/monochrome-icons'
import {
  Button,
  Cell,
  Dialog,
  Divider,
  Image,
  InputTextArea,
  Navigation,
  SelectionGroup,
  Spacer,
  Text,
  Validation,
} from '@vinted/web-ui'
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch } from '@marketplace-web/shared/api-client'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { useAsset } from '@marketplace-web/domain/assets'

import {
  transformAvailableCarriersResponse,
  getAvailableCarriers,
  getDeliveryType,
  createDeliveryOptionChangeRequest,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'

import GenericErrorModal from '../GenericErrorModal'
import RequestCarrierChangeConfirmationModal from '../RequestCarrierChangeConfirmationModal'

type Props = {
  show: boolean
  onClose: () => void
  onSubmit: () => void
}

const CarrierChangeModal = ({ show, onClose, onSubmit }: Props) => {
  const translate = useTranslate('carrier_change.modal')
  const asset = useAsset('/assets/shipping')

  const { conversation } = useConversationContext()

  const shipmentId = conversation?.transaction?.shipment?.id
  const shippingOrderId = conversation?.transaction?.shippingOrderId

  const [message, setMessage] = useState('')
  const [isDataLoading, setIsDataLoading] = useState(false)
  const [messageValidationError, setMessageValidationError] = useState('')
  const [carrierValidationError, setCarrierValidationError] = useState('')
  const [selectedCarrier, setSelectedCarrier] = useState('')
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [error, setError] = useState(false)

  const rootTestId = 'carrier-change-modal'
  const CARRIER_ICON_PLACEHOLDER = 'carrier-placeholder.svg'
  const MAX_LENGTH_CHARS = 200
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const {
    fetch: fetchAvailableCarriers,
    transformedData: availableCarriers,
    isLoading,
    error: availableCarriersError,
  } = useFetch(getAvailableCarriers, transformAvailableCarriersResponse)

  const fetchAvailableCarriersData = useCallback(async () => {
    if (!shipmentId || !show) return

    setIsDataLoading(true)

    const response = await getDeliveryType(shipmentId)

    if ('errors' in response) {
      setIsDataLoading(false)
      setError(true)

      return
    }

    fetchAvailableCarriers({
      shippingOrderId,
      selectedDeliveryType: response.delivery_type,
      shipmentId,
    })

    setIsDataLoading(false)
  }, [shipmentId, show, fetchAvailableCarriers, shippingOrderId])

  useEffect(() => {
    fetchAvailableCarriersData()
  }, [fetchAvailableCarriersData])

  const removeValidations = () => {
    setMessageValidationError('')
    setCarrierValidationError('')
    setSelectedCarrier('')
    setMessage('')
  }

  const handleClose = () => {
    removeValidations()
    onClose()
  }

  const handleSubmit = () => {
    setIsConfirmationModalOpen(false)
    removeValidations()
    onSubmit()
  }

  const validateSelectCarrier = () => {
    if (selectedCarrier) return true

    setCarrierValidationError(translate('errors.select_carrier'))

    return false
  }

  const handleConfirmationClick = () => {
    if (!validateSelectCarrier()) return

    if (messageValidationError) return

    setIsConfirmationModalOpen(true)
  }

  const handleConfirmationModalClose = () => setIsConfirmationModalOpen(false)

  const renderNavigation = () => {
    return (
      <>
        <Navigation
          testId={`${rootTestId}-navigation`}
          body={translate('modal_title')}
          right={
            <Button
              inline
              iconName={X24}
              styling={Button.Styling.Flat}
              onClick={handleClose}
              testId={`${rootTestId}-close-x-icon`}
              aria={{ 'aria-label': translate('actions.a11y.close') }}
            />
          }
        />
        <Divider />
      </>
    )
  }

  const renderTitle = () => {
    return (
      <>
        <Cell>
          <Spacer />
          <Text as="h1" text={translate('title')} type={Text.Type.Heading} />
          <Spacer />
          <Text as="span" text={translate('subtitle')} />
        </Cell>
        <Divider />
      </>
    )
  }

  const handleCarrierSelection = (rootId: string) => () => {
    setCarrierValidationError('')

    setSelectedCarrier(rootId)
  }

  const renderCarrierValidation = () => {
    if (!carrierValidationError) return null

    return (
      <div>
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Large} />
        <Text
          as="h4"
          type={Text.Type.Caption}
          theme="warning"
          text={carrierValidationError}
          testId={`${rootTestId}-select-carrier-validation`}
        />
        <Spacer />
      </div>
    )
  }

  const renderCarrierOptions = () => {
    return (
      <>
        <SelectionGroup layout={SelectionGroup.Layout.Default}>
          {availableCarriers?.map(availableCarrier => (
            <SelectionGroup.SelectionItem
              key={availableCarrier.rootRateUuid}
              prefix={
                <Image
                  src={availableCarrier.firstMileCarrier.iconUrl}
                  fallbackSrc={asset(CARRIER_ICON_PLACEHOLDER)}
                  size={Image.Size.Regular}
                  alt=""
                />
              }
              title={
                <Text
                  as="h2"
                  text={availableCarrier.firstMileCarrier.name}
                  type={Text.Type.Title}
                  testId={`${rootTestId}-carrier-name`}
                />
              }
              body={
                availableCarrier.firstMileCarrier.dropOffPointsUrl && (
                  <Text
                    as="span"
                    text={translate('find_more', {
                      'drop-off-points-link': chunks => (
                        <a
                          key="drop-off-points-link"
                          href={availableCarrier.firstMileCarrier.dropOffPointsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {chunks}
                        </a>
                      ),
                    })}
                  />
                )
              }
              testId={`${rootTestId}-carrier-option-${availableCarrier.title}`}
              onClick={handleCarrierSelection(availableCarrier.rootRateUuid)}
              isSelected={selectedCarrier === availableCarrier.rootRateUuid}
            />
          ))}
        </SelectionGroup>
        {renderCarrierValidation()}
        <Divider />
      </>
    )
  }

  const validateInput = (inputValue: string): string => {
    const inputValueLength = inputValue.trim().length

    if (inputValueLength > MAX_LENGTH_CHARS) {
      const charactersOverTheLimit = { count: inputValueLength - MAX_LENGTH_CHARS }

      return translate('errors.message_too_long', charactersOverTheLimit, charactersOverTheLimit)
    }

    return ''
  }

  const renderValidation = () => {
    if (messageValidationError) return <Validation text={messageValidationError} theme="warning" />

    const remainingCharacterCount = { count: MAX_LENGTH_CHARS - message.length }

    return translate('hint', remainingCharacterCount, remainingCharacterCount)
  }

  const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const { value: inputValue } = event.target

    setMessageValidationError(validateInput(inputValue))
    setMessage(inputValue)
  }

  const renderMessage = () => (
    <InputTextArea
      name="message"
      placeholder={translate('message_placeholder')}
      title={translate('message_title')}
      onChange={handleInputChange}
      validation={renderValidation()}
      ref={inputRef}
      testId={`${rootTestId}-message`}
    />
  )

  const handleRequestCarrierChange = async () => {
    if (!shipmentId) return

    const response = await createDeliveryOptionChangeRequest({
      shipmentId,
      rootRateUuid: selectedCarrier,
      reason: message,
    })

    if ('errors' in response) {
      setCarrierValidationError(response.errors[0]!.value)

      return
    }

    handleSubmit()
  }

  function renderActions() {
    return (
      <Cell>
        <Button
          text={translate('actions.request_carrier_change')}
          styling={Button.Styling.Filled}
          onClick={handleConfirmationClick}
          testId={`${rootTestId}-request-carrier-change-button`}
        />
        <Spacer />
        <Button
          text={translate('actions.back')}
          styling={Button.Styling.Flat}
          onClick={handleClose}
          testId={`${rootTestId}-cancel-button`}
        />
      </Cell>
    )
  }

  function renderEmptyState() {
    return (
      <Dialog show>
        {renderNavigation()}
        <Cell>
          <Spacer />
          <div className="u-text-center">
            <Text
              as="h1"
              type={Text.Type.Heading}
              alignment={Text.Alignment.Center}
              text={translate('empty_state.title')}
            />
          </div>
          <Spacer size={Spacer.Size.Large} />
          <Text
            as="span"
            type={Text.Type.Body}
            alignment={Text.Alignment.Center}
            text={translate('empty_state.body')}
          />
          <Spacer size={Spacer.Size.XLarge} />
        </Cell>
      </Dialog>
    )
  }

  if (!show) return null

  if (availableCarriers?.length === 0) return renderEmptyState()

  if (availableCarriersError || error) return <GenericErrorModal isShown onClose={handleClose} />

  if (isConfirmationModalOpen) {
    return (
      <RequestCarrierChangeConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleConfirmationModalClose}
        onConfirm={handleRequestCarrierChange}
      />
    )
  }

  if (isLoading || isDataLoading)
    return (
      <Dialog show={show} testId={`${rootTestId}-loader`}>
        <ContentLoader />
      </Dialog>
    )

  return (
    <Dialog show={show} testId={rootTestId} hasScrollableContent>
      <div className="u-fill-width">
        <ScrollableArea>
          {renderNavigation()}
          {renderTitle()}
          {renderCarrierOptions()}
          {renderMessage()}
          {renderActions()}
        </ScrollableArea>
      </div>
    </Dialog>
  )
}

export default CarrierChangeModal
